import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaExternalLinkAlt, FaCheck, FaChevronRight, FaChevronDown } from 'react-icons/fa';

// Styled components
const PageContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  color: white;
  overflow: hidden;
  
  /* Enhanced animated gradient background */
  @keyframes gradientAnimation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
  
  /* Increased contrast between color stops for more visible animation */
  background: linear-gradient(135deg, #0D0E1C 0%, #20233D 35%, #0D0E1C 70%, #20233D 100%);
  background-size: 300% 300%;
  animation: gradientAnimation 8s ease infinite; /* Faster animation speed */
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${props => props.bgImage || ''});
    background-position: center;
    background-size: cover;
    opacity: 0.2;
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 80%;
    height: 200%;
    /* Increased color intensity for more visibility */
    background: radial-gradient(ellipse at center, rgba(255, 75, 140, 0.35) 0%, rgba(0, 85, 255, 0.4) 50%, rgba(0, 0, 0, 0) 70%);
    transform: rotate(-20deg);
    z-index: 1;
    filter: blur(60px);
    animation: pulseGlow 5s ease-in-out infinite alternate; /* Faster animation */
  }
  
  @keyframes pulseGlow {
    0% {
      opacity: 0.7;
      filter: blur(60px);
      transform: rotate(-20deg) scale(0.95);
    }
    50% {
      opacity: 1;
      filter: blur(70px);
      transform: rotate(-22deg) scale(1.05); /* More rotation for more evident movement */
    }
    100% {
      opacity: 0.7;
      filter: blur(60px);
      transform: rotate(-18deg) scale(0.95); /* Rotating in both directions */
    }
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
`;

const FloatingObjects = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
`;

const FloatingObject = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: ${props => props.bg || 'rgba(255, 255, 255, 0.1)'};
  filter: blur(${props => props.blur || '0px'});
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 8rem 2rem;
  width: 100%;
  position: relative;
  z-index: 3;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  text-decoration: none;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  
  svg {
    margin-right: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: white;
    
    svg {
      transform: translateX(-5px);
    }
  }
`;

const ProjectCategory = styled.span`
  background-color: rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.accent};
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 1.5rem;
`;

const ProjectTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  position: relative;
  line-height: 1.2;
  max-width: 70%;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    max-width: 100%;
  }
`;

const ProjectDescription = styled(motion.p)`
  font-size: 1.3rem;
  max-width: 700px;
  margin-bottom: 2.5rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
`;

const CTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 85, 255, 0.3);
  
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(0, 85, 255, 0.4);
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const DetailSection = styled.section`
  padding: 8rem 2rem;
  background-color: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContentColumn = styled.div``;

const SidebarColumn = styled.div``;

const SectionHeading = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.text};
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: linear-gradient(to right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
    margin-top: 0.8rem;
    border-radius: 2px;
  }
`;

const FullDescription = styled.div`
  margin-bottom: 4rem;
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
`;

const FeatureList = styled.ul`
  margin-bottom: 4rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.2rem;
  font-size: 1.1rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
    margin-right: 1rem;
    margin-top: 0.3rem;
    min-width: 1rem;
  }
`;

const TechCard = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const TechCardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.text};
  font-weight: 700;
`;

const TechTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const TechTag = styled.span`
  background-color: ${props => props.theme.colors.backgroundAlt};
  color: ${props => props.theme.colors.textMedium};
  padding: 0.6rem 1.2rem;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const InfoCard = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
`;

const InfoCardTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.textMedium};
  font-weight: 600;
`;

const InfoCardValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const GallerySection = styled.section`
  padding: 4rem 0 8rem;
  background-color: ${props => props.theme.colors.background};
`;

const GallerySectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: ${props => props.theme.colors.text};
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
    margin: 1rem auto 0;
    border-radius: 2px;
  }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled(motion.div)`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  height: 250px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 10rem 2rem;
  
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }
  
  p {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.textLight};
    max-width: 600px;
    margin: 0 auto 2rem;
  }
`;

const ScrollDownButton = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: 140px; /* Set a specific width */
  z-index: 10;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  
  svg {
    font-size: 1.5rem;
    margin-top: 8px;
  }
  
  span {
    font-size: 0.9rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-weight: 500;
  }
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    } 
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.8, 
      ease: [0.22, 1, 0.36, 1] 
    } 
  }
};

const ProjectDetail = ({ setCursorVariant }) => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Reference for the detail section
  const detailSectionRef = useRef(null);
  
  useEffect(() => {
    // In a real application, this would be an API call
    // Here we're simulating fetching the project data
    const fetchProject = async () => {
      try {
        setLoading(true);
        
        // Normally you would fetch this from an API
        // For now, we're importing it from the Portfolio component
        const projects = [
          {
            id: 1,
            title: "E-commerce Platform",
            slug: "ecommerce-platform",
            category: "Web Development",
            description: "A modern e-commerce platform with user authentication, product management, and payment processing integration.",
            image: "https://images.unsplash.com/photo-1661956602139-ec64991b8b16?ixlib=rb-4.0.3&ixid=MnwxMjA3fDF8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=665&q=80",
            link: "#",
            tags: ["React", "Node.js", "MongoDB", "Stripe"],
            fullDescription: "Our e-commerce platform provides a complete solution for online retail. The system features user authentication with multiple roles, comprehensive product management, inventory tracking, shopping cart functionality, secure payment processing with Stripe integration, order management, and detailed analytics dashboards. The platform is built with a React frontend and Node.js backend with MongoDB for flexible data storage.",
            features: [
              "User authentication and role-based access control",
              "Product management with categories and tags",
              "Inventory tracking and management",
              "Shopping cart with persistent storage",
              "Secure payment processing",
              "Order management and tracking",
              "Admin dashboard with analytics"
            ],
            technologies: ["React", "Redux", "Node.js", "Express", "MongoDB", "Stripe API", "JWT Authentication", "AWS S3 for image storage"],
            screenshots: [
              "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1627850604058-52e40a14eaea?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1074&q=80",
              "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
            ],
            client: "RetailTech Inc.",
            duration: "4 months",
            completionDate: "January 2023"
          },
          {
            id: 2,
            title: "Financial Dashboard",
            slug: "financial-dashboard",
            category: "Software Solutions",
            description: "An interactive financial dashboard for tracking investments, expenses, and budget planning with data visualization.",
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
            link: "#",
            tags: ["Angular", "Chart.js", "Firebase", "TypeScript"],
            fullDescription: "The Financial Dashboard is a comprehensive tool for personal and business financial management. It allows users to track investments, monitor expenses, and plan budgets through interactive visualizations. The dashboard integrates with various financial APIs to provide real-time data on stocks, cryptocurrency, and other investments. Users can set financial goals, receive alerts, and generate detailed reports for better financial planning.",
            features: [
              "Real-time investment tracking",
              "Expense categorization and analysis",
              "Budget planning and monitoring",
              "Financial goal setting",
              "Interactive data visualizations",
              "Report generation",
              "Mobile-responsive design"
            ],
            technologies: ["Angular", "TypeScript", "Chart.js", "Firebase", "Financial APIs", "Progressive Web App", "Responsive Design"],
            screenshots: [
              "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1115&q=80",
              "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1565514020179-026b5cfdec5c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
            ],
            client: "FinTech Solutions",
            duration: "6 months",
            completionDate: "March 2023"
          },
          {
            id: 3,
            title: "Delivery Tracking App",
            slug: "delivery-tracking-app",
            category: "Mobile Apps",
            description: "A mobile application for real-time tracking of deliveries with push notifications and route optimization features.",
            image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
            link: "#",
            tags: ["React Native", "Redux", "Google Maps API", "Firebase"],
            fullDescription: "The Delivery Tracking App provides a comprehensive solution for logistics companies and customers to track packages in real-time. For delivery personnel, the app offers route optimization, delivery scheduling, and proof of delivery functionality. For customers, it provides real-time tracking, delivery estimates, and push notifications for status updates. The application is built with React Native for cross-platform compatibility and uses Google Maps API for accurate location tracking.",
            features: [
              "Real-time package tracking",
              "Route optimization for drivers",
              "Delivery scheduling and management",
              "Push notifications for status updates",
              "Proof of delivery with digital signatures",
              "Delivery history and analytics",
              "Customer feedback and ratings"
            ],
            technologies: ["React Native", "Redux", "Google Maps API", "Firebase", "Push Notifications", "Geolocation Services", "Cross-platform development"],
            screenshots: [
              "https://images.unsplash.com/photo-1596558450268-9c27524ba856?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1607463747820-6fa7e25283c1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1614315517650-3771cf72d18a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1044&q=80"
            ],
            client: "LogiTrack LLC",
            duration: "5 months",
            completionDate: "November 2022"
          },
          {
            id: 4,
            title: "Project Management Tool",
            slug: "project-management-tool",
            category: "Web Development",
            description: "A comprehensive project management tool with task assignment, progress tracking, and collaboration features for teams.",
            image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
            link: "#",
            tags: ["Vue.js", "Express", "PostgreSQL", "Socket.io"],
            fullDescription: "Our Project Management Tool is designed to streamline team collaboration and project workflows. It features task boards with drag-and-drop functionality, time tracking, file sharing, and real-time updates through WebSockets. Teams can create project timelines, assign tasks with priorities, track progress through customizable dashboards, and communicate within the platform through comments and messaging. The tool integrates with popular services like Google Calendar, GitHub, and Slack for a seamless workflow.",
            features: [
              "Kanban-style task boards",
              "Time tracking and reporting",
              "File sharing and document management",
              "Project timelines and Gantt charts",
              "Real-time collaboration and updates",
              "Role-based permissions",
              "Integration with third-party services"
            ],
            technologies: ["Vue.js", "Vuex", "Express", "PostgreSQL", "Socket.io", "RESTful API", "OAuth 2.0", "AWS Hosting"],
            screenshots: [
              "https://images.unsplash.com/photo-1506784926709-22f1ec395907?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1168&q=80",
              "https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
            ],
            client: "TeamSync Inc.",
            duration: "7 months",
            completionDate: "June 2023"
          },
          {
            id: 5,
            title: "Healthcare Portal",
            slug: "healthcare-portal",
            category: "Software Solutions",
            description: "A secure healthcare portal for patient management, appointment scheduling, and medical record access with strict privacy controls.",
            image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
            link: "#",
            tags: ["React", "Node.js", "MySQL", "AWS"],
            fullDescription: "The Healthcare Portal is a HIPAA-compliant solution for healthcare providers to manage patient data, scheduling, and medical records. The platform features secure patient portals, appointment scheduling with reminders, electronic health records (EHR) management, billing integration, and secure messaging between providers and patients. The system employs advanced security measures including end-to-end encryption, audit logging, and role-based access control to ensure patient data privacy and compliance with healthcare regulations.",
            features: [
              "Secure patient portal access",
              "Appointment scheduling and management",
              "Electronic health records (EHR)",
              "Prescription management",
              "Secure messaging system",
              "Billing and insurance integration",
              "Compliance reporting and auditing"
            ],
            technologies: ["React", "Node.js", "Express", "MySQL", "AWS HIPAA-eligible services", "End-to-end encryption", "OAuth 2.0 with MFA", "RESTful API"],
            screenshots: [
              "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=880&q=80"
            ],
            client: "MediSafe Solutions",
            duration: "9 months",
            completionDate: "February 2023"
          },
          {
            id: 6,
            title: "Fitness Tracking App",
            slug: "fitness-tracking-app",
            category: "Mobile Apps",
            description: "A mobile app for tracking workouts, nutrition, and health metrics with personalized recommendations based on user activity.",
            image: "https://images.unsplash.com/photo-1574259392081-dbe3c19cd15c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=677&q=80",
            link: "#",
            tags: ["Flutter", "Dart", "Firebase", "RESTful API"],
            fullDescription: "The Fitness Tracking App is a comprehensive solution for health and wellness management. It enables users to track workouts, log nutrition intake, monitor health metrics, and receive personalized recommendations. The app connects with wearable devices for automatic activity tracking, provides customized workout plans based on user goals, offers meal planning with nutritional analysis, and visualizes progress through interactive charts. Built with Flutter, the app delivers a native-like experience on both iOS and Android platforms while maintaining a single codebase.",
            features: [
              "Workout tracking and planning",
              "Nutrition logging and meal planning",
              "Health metrics monitoring",
              "Integration with wearable devices",
              "Personalized recommendations",
              "Progress visualization",
              "Social features and challenges"
            ],
            technologies: ["Flutter", "Dart", "Firebase", "RESTful API", "Health device integration", "Machine learning for recommendations", "Cross-platform development"],
            screenshots: [
              "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1604480133435-25b86862d276?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80",
              "https://images.unsplash.com/photo-1593079831268-3381b0db4a77?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1169&q=80"
            ],
            client: "FitLife Technologies",
            duration: "6 months",
            completionDate: "April 2023"
          }
        ];
        
        const foundProject = projects.find(p => p.slug === slug);
        setProject(foundProject);
        
        // Set page title
        if (foundProject) {
          document.title = `${foundProject.title} | D-Soft Portfolio`;
        } else {
          document.title = "Project Not Found | D-Soft Portfolio";
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching project: ", error);
        setLoading(false);
      }
    };
    
    fetchProject();
    
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Handle scroll down click
  const handleScrollDown = () => {
    detailSectionRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!project) {
    return (
      <NotFoundMessage>
        <h2>Project Not Found</h2>
        <p>We couldn't find the project you're looking for. It may have been moved or doesn't exist.</p>
        <BackButton to="/portfolio">
          <FaArrowLeft /> Back to Portfolio
        </BackButton>
      </NotFoundMessage>
    );
  }

  return (
    <PageContainer>
      <HeroSection bgImage={project.image}>
        <HeroOverlay />
        <FloatingObjects>
          <FloatingObject 
            as={motion.div}
            animate={{ 
              y: [0, -50, 0], 
              x: [0, 40, 0],
              scale: [1, 1.3, 1],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 6, /* Faster animation */
              ease: "easeInOut" 
            }}
            style={{ 
              top: '20%', 
              left: '10%', 
              width: '300px', /* Larger size */
              height: '300px' 
            }}
            bg="radial-gradient(circle, rgba(0,85,255,0.2) 0%, rgba(0,85,255,0) 70%)" /* More opacity */
            blur="50px"
          />
          <FloatingObject 
            as={motion.div}
            animate={{ 
              y: [0, 70, 0], 
              x: [0, -40, 0],
              scale: [1, 1.4, 1],
              opacity: [0.6, 1, 0.6]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 7, /* Faster animation */
              ease: "easeInOut"
            }}
            style={{ 
              top: '60%', 
              right: '15%', 
              width: '350px', /* Larger size */
              height: '350px' 
            }}
            bg="radial-gradient(circle, rgba(255,75,140,0.2) 0%, rgba(255,75,140,0) 70%)" /* More opacity */
            blur="60px"
          />
          <FloatingObject 
            as={motion.div}
            animate={{ 
              y: [0, 45, 0], 
              x: [0, 45, 0],
              scale: [0.9, 1.35, 0.9], /* More dramatic scale change */
              opacity: [0.6, 1, 0.6]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 5, /* Faster animation */
              ease: "easeInOut"
            }}
            style={{ 
              top: '35%', 
              left: '65%', 
              width: '280px', /* Larger size */
              height: '280px' 
            }}
            bg="radial-gradient(circle, rgba(100,200,255,0.25) 0%, rgba(100,200,255,0) 70%)" /* More opacity */
            blur="45px"
          />
          {/* New additional floating object for more visual impact */}
          <FloatingObject 
            as={motion.div}
            animate={{ 
              y: [0, -35, 0], 
              x: [0, -35, 0],
              scale: [0.8, 1.2, 0.8],
              opacity: [0.5, 0.9, 0.5]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 8,
              ease: "easeInOut"
            }}
            style={{ 
              top: '70%', 
              left: '25%', 
              width: '250px', 
              height: '250px' 
            }}
            bg="radial-gradient(circle, rgba(180,130,255,0.2) 0%, rgba(180,130,255,0) 70%)" /* Purple hue */
            blur="50px"
          />
        </FloatingObjects>
        <HeroContent>
          <BackButton 
            to="/portfolio" 
            onMouseEnter={() => setCursorVariant && setCursorVariant("button")}
            onMouseLeave={() => setCursorVariant && setCursorVariant("default")}
          >
            <FaArrowLeft /> Back to Portfolio
          </BackButton>
          
          <motion.div
            initial="hidden"
            animate="visible"
          >
            <ProjectCategory>{project.category}</ProjectCategory>
            <ProjectTitle variants={fadeInUp}>{project.title}</ProjectTitle>
            <ProjectDescription variants={fadeInUp}>{project.description}</ProjectDescription>
            
            <motion.div variants={fadeInUp}>
              <CTAButton 
                href={project.link} 
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setCursorVariant && setCursorVariant("button")}
                onMouseLeave={() => setCursorVariant && setCursorVariant("default")}
              >
                Visit Project <FaExternalLinkAlt />
              </CTAButton>
            </motion.div>
          </motion.div>
        </HeroContent>
        
        <ScrollDownButton
          onClick={handleScrollDown}
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          whileHover={{ scale: 1.1 }}
          onMouseEnter={() => setCursorVariant && setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant && setCursorVariant("default")}
        >
          <span>Project Details</span>
          <FaChevronDown />
        </ScrollDownButton>
      </HeroSection>

      <DetailSection ref={detailSectionRef}>
        <Container>
          <DetailGrid>
            <ContentColumn>
              <SectionHeading>About this Project</SectionHeading>
              <FullDescription>
                <p>{project.fullDescription}</p>
              </FullDescription>
              
              <SectionHeading>Key Features</SectionHeading>
              <FeatureList>
                {project.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FaCheck />
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
              
              <TechCard>
                <TechCardTitle>Technologies Used</TechCardTitle>
                <TechTags>
                  {project.technologies.map((tech, index) => (
                    <TechTag key={index}>{tech}</TechTag>
                  ))}
                </TechTags>
              </TechCard>
            </ContentColumn>
            
            <SidebarColumn>
              <InfoCard>
                <InfoCardTitle>Client</InfoCardTitle>
                <InfoCardValue>{project.client}</InfoCardValue>
              </InfoCard>
              
              <InfoCard>
                <InfoCardTitle>Project Duration</InfoCardTitle>
                <InfoCardValue>{project.duration}</InfoCardValue>
              </InfoCard>
              
              <InfoCard>
                <InfoCardTitle>Completion Date</InfoCardTitle>
                <InfoCardValue>{project.completionDate}</InfoCardValue>
              </InfoCard>
              
              <InfoCard>
                <InfoCardTitle>Project Tags</InfoCardTitle>
                <TechTags>
                  {project.tags.map((tag, index) => (
                    <TechTag key={index}>{tag}</TechTag>
                  ))}
                </TechTags>
              </InfoCard>
            </SidebarColumn>
          </DetailGrid>
        </Container>
      </DetailSection>
      
      <GallerySection>
        <Container>
          <GallerySectionTitle>Project Gallery</GallerySectionTitle>
          <Gallery>
            {project.screenshots.map((screenshot, index) => (
              <GalleryItem 
                key={index}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => setCursorVariant && setCursorVariant("image")}
                onMouseLeave={() => setCursorVariant && setCursorVariant("default")}
              >
                <img src={screenshot} alt={`${project.title} screenshot ${index + 1}`} />
              </GalleryItem>
            ))}
          </Gallery>
        </Container>
      </GallerySection>
    </PageContainer>
  );
};

export default ProjectDetail; 